import React, { useMemo, createContext, ReactNode } from 'react';

import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { SelectionCategory } from 'modules/shared-menu/types';

type WoltSpecialsContextValue = {
  woltSpecialsCategory: SelectionCategory | undefined;
  woltSpecialsOfferIds: Set<string>;
  categoriesWithoutWoltSpecials: SelectionCategory[];
};

const WoltSpecialsContext = createContext<WoltSpecialsContextValue>({} as WoltSpecialsContextValue);

export const WoltSpecialsContextProvider = ({ children }: { children: ReactNode }) => {
  const { categories } = useSelectionData();

  const woltSpecialsCategory = useMemo(() => categories.find(c => !!c.is_wolt_special), [categories]);

  const woltSpecialsOfferIds = useMemo(
    () => new Set(woltSpecialsCategory?.offer_bindings?.map(ob => ob.offer_id) ?? []),
    [woltSpecialsCategory],
  );

  const categoriesWithoutWoltSpecials = useMemo(() => categories.filter(c => !c.is_wolt_special), [categories]);

  return (
    <WoltSpecialsContext.Provider
      value={{
        woltSpecialsCategory,
        woltSpecialsOfferIds,
        categoriesWithoutWoltSpecials,
      }}
    >
      {children}
    </WoltSpecialsContext.Provider>
  );
};

export const useWoltSpecials = () => {
  if (!WoltSpecialsContext) {
    throw new Error('useWoltSpecials must be used within a WoltSpecialsContextProvider');
  }

  return React.useContext(WoltSpecialsContext);
};
